import React, {useCallback} from 'react'
import {useDispatch} from 'react-redux'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {OverlayType} from 'modules/overlay/types'

import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {SkeletonLoader} from '@vio/atlas/src/components/designSystem/SkeletonLoader'
import {Text} from '@vio/atlas/Text'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {getRoundingStrategy} from '@vio/core/src/utils/number'
import {Price} from '@vio/shared/src/search/Price'
import {ProviderLogo} from '@vio/shared/src/search/ProviderLogo'

import {useGetNextTopProviders} from '../../useGetNextTopProviders'
import {useGetNonTopCheapestOfferPrice} from '../../useGetNonTopCheapestOfferPrice'

const ICON_OVERLAP_MARGIN = '-7px'

export const MoreDealsDesktopButton = ({hotelId}: {hotelId: string}) => {
  const showOverlay = useShowOverlay()
  const dispatch = useDispatch()
  const {value, currency, isLoading} = useGetNonTopCheapestOfferPrice({hotelId})
  const topProviders = useGetNextTopProviders({hotelId})

  const onShowAllOffersClick = useCallback(() => {
    showOverlay({
      type: OverlayType.HotelAllOffers
    })

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelMoreDealsOption,
        action: Action.Clicked
      })
    )
  }, [dispatch, showOverlay])

  return (
    <Button
      variant="ghostInverse"
      size="xl"
      fullWidth
      iconEnd={
        <Icon
          name="ChevronRight"
          colorPath="content.neutral.primary"
          size="md"
        />
      }
      onClick={onShowAllOffersClick}
    >
      <Box display="flex" flexDirection="row" gap="s300">
        <Box
          height="25px"
          display="flex"
          gap="s200"
          flexDirection="row"
          alignItems="center"
        >
          {topProviders.map((offerProvider, index, items) => (
            <Box
              key={index}
              marginRight={
                index === items.length - 1 ? '0' : ICON_OVERLAP_MARGIN
              }
            >
              <ProviderLogo
                providerCode={offerProvider}
                height={21}
                variant="circle"
              />
            </Box>
          ))}
        </Box>
        <Text variant="titleM" colorPath="content.neutral.primary">
          <FormattedMessageWrapper
            defaultMessage="More deals from {price}"
            id="moreDealsFromPrice"
            values={{
              price: !isLoading ? (
                <Price
                  style={{display: 'inline-block'}}
                  value={value}
                  currency={currency}
                  size="md"
                  roundingStrategy={getRoundingStrategy()}
                />
              ) : (
                <SkeletonLoader width={60} style={{display: 'inline-block'}} />
              )
            }}
          />
        </Text>
      </Box>
    </Button>
  )
}
